import {
  IMigrationDestination,
  ISourceEntityRecordFilter,
  ISourceEntityTypes,
  SourceEntityRecordStatus,
  type IExpectedSourceRecordSize,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntity,
  type ISourceEntityHandler,
  type ISourceEntityRecord,
  type ISourceSyncErrorData,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Postgres,
  WithPrimaryKey,
  errorNil,
  find$,
  getEnumValues,
  multiMap,
  snapshot,
  toTimestamp,
  where,
  type IReffable,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { flow, isEqual } from 'lodash';
import { from, of, type Observable } from 'rxjs';
import { catchError, concatMap, map, scan, take } from 'rxjs/operators';
import { PracticeMigration } from '../practice-migrations';
import { runQuery } from './connection';
import { PostgresQueryCache } from './postgres-query-cache';
import { SourceEntity } from './source-entity';
import { SourceEntityRecord } from './source-entity-record';
import {
  convertDateToTimestampFn,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  IKeysetCursorRef,
  KEYSET_PLACEHOLDER,
  OFFSET_PLACEHOLDER,
  runSourceQuery$,
} from './source-helpers';

export const TRANSFORM_DATA_FN_DEFAULTS = flow([
  convertKeysToCamelCaseFn(),
  convertNullToUndefinedFn(),
  convertDateToTimestampFn(),
]);

export abstract class BaseSourceEntity<Source extends ISourceEntityTypes>
  implements ISourceEntityHandler<Source>
{
  abstract sourceEntity: ISourceEntity;
  abstract entityResourceType: string;
  abstract sourceQuery: string | string[];
  abstract verifySourceFn: (
    data: unknown,
    logError: boolean
  ) => data is Source['data'];
  estimateQuery?: string | string[];
  requiredEntities = {};
  defaultOffsetSize?: number;
  keysetPagination?: { cursorColumn: string; cursorField?: string };

  /**
   * Populated when `keysetPagination` is configured. Holds the last-seen
   * cursor id after a `getFromSource$` run completes. Read by
   * `publishSourceJob` to advance `queryOffset` for the next outer window.
   */
  readonly keysetCursorRef: IKeysetCursorRef = {};

  transformDataFn: (records: Source['data'][]) => Source['data'][] =
    TRANSFORM_DATA_FN_DEFAULTS;

  getFromSource$(
    migration: WithRef<IPracticeMigration>,
    initialOffset: number = 0,
    endOffset?: number
  ): Observable<Source['data'][] | ISourceSyncErrorData> {
    return runSourceQuery$<Source['data'], Source['data']>(
      migration,
      this.sourceQuery,
      this.transformDataFn,
      this.defaultOffsetSize,
      initialOffset,
      endOffset,
      undefined,
      this.keysetPagination,
      this.keysetPagination ? this.keysetCursorRef : undefined
    );
  }

  abstract getSourceRecordId(data: Source['data']): string | number;
  abstract getSourceLabel(record: Source['data']): string;
  abstract translate(
    initialValue: Source['data'],
    timezone: Timezone
  ): Source['translations'];

  verifySource(
    data: unknown,
    logError: boolean = false
  ): data is Source['data'] {
    return this.verifySourceFn(data, logError);
  }

  getEntity$(
    migration: IReffable<IPracticeMigration>
  ): Observable<ISourceEntity | undefined> {
    return find$(
      PracticeMigration.sourceEntityCol(migration),
      where('metadata', '==', this.sourceEntity.metadata)
    );
  }

  getMapResourceType(): string {
    return this.entityResourceType;
  }

  canHandle(source: ISourceEntity): boolean {
    return isEqual(
      source.metadata.idPrefix,
      this.sourceEntity.metadata.idPrefix
    );
  }

  getSourceEntity(
    migration: IReffable<IPracticeMigration>
  ): Promise<WithRef<ISourceEntity>> {
    return snapshot(
      PracticeMigration.findSourceEntity$(migration, this.sourceEntity).pipe(
        take(1),
        errorNil(
          `Source entity not found for ${this.sourceEntity.metadata.label}`
        )
      )
    );
  }

  combineRecordWithData(
    record: WithPrimaryKey<ISourceEntityRecord<Source>>
  ): IGetRecordResponse<Source> {
    return {
      record,
      data: SourceEntityRecord.getLatestData<Source['data']>(record),
    };
  }

  getRecord(
    migration: WithRef<IPracticeMigration>,
    recordId: string | number
  ): Promise<IGetRecordResponse<Source> | undefined> {
    return snapshot(
      from(this.getSourceEntity(migration)).pipe(
        concatMap(async (sourceEntity) =>
          SourceEntity.getSourceRecordEntityByUid<Source>(
            migration,
            sourceEntity,
            SourceEntity.determineUidForRecord(recordId, this.sourceEntity)
          )
        ),
        errorNil(
          `Record not found for ${this.sourceEntity.metadata.label} recordId: ${recordId}`
        ),
        map((record) => this.combineRecordWithData(record)),
        catchError(() => of(undefined))
      )
    );
  }

  /**
   * Returns records in batches. Each batch is emitted separately.
   *
   * ⚠️ WARNING: When using with .toPromise(), you will only get the LAST batch!
   * Use getAllRecords$() instead if you need all records accumulated.
   *
   * @see getAllRecords$ for getting all records at once
   */
  getRecords$<FilterValue>(
    migration: WithRef<IPracticeMigration>,
    bufferSize: number = 50,
    limit?: number,
    migrationStatus?: IMigrationDestination,
    filter?: ISourceEntityRecordFilter<FilterValue>
  ): Observable<IGetRecordResponse<Source>[]> {
    return from(this.getSourceEntity(migration)).pipe(
      concatMap((sourceEntity) =>
        Postgres.bufferedQuery$(
          SourceEntity.records.bind(SourceEntity)<Source, FilterValue>,
          {
            migration,
            sourceEntity,
            statuses: getEnumValues(SourceEntityRecordStatus),
            migrationStatus,
            filter,
            limit: bufferSize,
          },
          undefined,
          limit
        )
      ),
      multiMap((record) => this.combineRecordWithData(record))
    );
  }

  /**
   * Returns ALL records accumulated from all batches.
   * Unlike getRecords$() which emits each batch separately, this method
   * uses scan() to accumulate all batches. Safe to use with toPromise()
   * since toPromise() returns the last emitted value.
   *
   * @example
   * // Get all records for a migration
   * const records = await sourceHandler.getAllRecords$(migration).toPromise();
   */
  getAllRecords$<FilterValue>(
    migration: WithRef<IPracticeMigration>,
    bufferSize: number = 1000,
    limit?: number,
    migrationStatus?: IMigrationDestination,
    filter?: ISourceEntityRecordFilter<FilterValue>
  ): Observable<IGetRecordResponse<Source>[]> {
    return this.getRecords$(
      migration,
      bufferSize,
      limit,
      migrationStatus,
      filter
    ).pipe(
      scan(
        (all, batch) => [...all, ...batch],
        [] as IGetRecordResponse<Source>[]
      )
    );
  }

  async getExpectedRecordSize(
    migration: WithRef<IPracticeMigration>
  ): Promise<IExpectedSourceRecordSize> {
    const queries = this._getEstimateQuery();

    const response = await runQuery<{ count: number }>(
      migration,
      queries.map((query) => {
        const stripped = query
          .replace(OFFSET_PLACEHOLDER, '')
          .replace(KEYSET_PLACEHOLDER, '');
        return `SELECT COUNT(*) FROM (${stripped}) src`;
      })
    );
    return {
      expectedSize: response.rows[0].count,
      expectedSizeCalculatedAt: toTimestamp(),
    };
  }

  async filterRecords<K extends keyof Source['filters']>(
    migration: WithRef<IPracticeMigration>,
    filterKey: K,
    value: Source['filters'][K],
    filterFn?: (record: IGetRecordResponse<Source>) => boolean
  ): Promise<IGetRecordResponse<Source>[]> {
    const cacheKey = `${this.sourceEntity.metadata.idPrefix}-${String(
      filterKey
    )}-${String(value)}`;

    const records = await PostgresQueryCache.get<IGetRecordResponse<Source>>(
      migration,
      cacheKey,
      () => this._filterRecordsQuery(migration, filterKey, value)
    );

    return filterFn ? records.filter(filterFn) : records;
  }

  private _filterRecordsQuery<K extends keyof Source['filters']>(
    migration: WithRef<IPracticeMigration>,
    filterKey: K,
    value: Source['filters'][K]
  ): Promise<IGetRecordResponse<Source>[]> {
    return this.getRecords$(migration, 1000, undefined, undefined, {
      key: String(filterKey),
      value: value,
    })
      .pipe(
        errorNil(
          `No records found for ${
            this.sourceEntity.metadata.label
          } ${filterKey.toString()}: ${JSON.stringify(value)}`
        ),
        scan(
          (allRecords, records) => [...allRecords, ...records],
          [] as IGetRecordResponse<Source>[]
        )
      )
      .toPromise();
  }

  private _getEstimateQuery(): string[] {
    if (!this.estimateQuery) {
      return Array.isArray(this.sourceQuery)
        ? this.sourceQuery
        : [this.sourceQuery];
    }
    return Array.isArray(this.estimateQuery)
      ? this.estimateQuery
      : [this.estimateQuery];
  }
}
